import React, { memo, useContext, useEffect, useRef, useState } from 'react';
import { FINISH_TAG, SprintContext } from '../../SprintContext';

const styles = {
    header: {
        position: 'sticky',
        top: 0,
        zIndex: 1,
        padding: '8px 12px',
    },
    headerEnd: {
        position: 'sticky',
        top: 0,
        zIndex: 1,
        padding: '8px 12px',
        textAlign: 'end',
    },
    stickyRow: {
        position: 'sticky',
        top: '48hi px',
        zIndex: 2,
    },
};

const MAX_LAPTIME = 60;

const GenericTable = memo(({ doAutoScroll, laps, singular, type, fastest200mAllTime = null }) => {
    const tableRef = useRef(null);
    const intervalRef = useRef(null);

    const [isScrolling, setIsScrolling] = useState(true);

    const { findTagMapping } = useContext(SprintContext);

    // Higher is faster.
    // Don't set <= 1. For some reason it breakes the scrolling.
    const scrollSpeed = 1;

    const startScrolling = () => {
        if (tableRef.current && isScrolling && !singular) {
            const container = tableRef.current;

            intervalRef.current = setInterval(() => {
                if (container.scrollHeight > container.clientHeight) {
                    container.scrollTop += scrollSpeed;

                    if (container.scrollTop + container.clientHeight - 30 > container.scrollHeight) {
                        //console.log(container.scrollTop + container.clientHeight, container.scrollHeight);
                        clearInterval(intervalRef.current);
                        setIsScrolling(false);

                        // After 1 second, restart scrolling from the top
                        setTimeout(() => {
                            container.scrollTop = 0;
                            setIsScrolling(true);
                            startScrolling();
                        }, 1000);
                    }
                    if (container.scrollTop + container.clientHeight === container.scrollHeight) {
                        clearInterval(intervalRef.current);
                        // After 1 second, restart scrolling from the top
                        setTimeout(() => {
                            container.scrollTop = 0;
                            setIsScrolling();
                            if (container.clientHeight - 50 > container.scrollHeight) {
                                startScrolling();
                            }
                        }, 1000);
                    }

                }
            }, 40); // 40ms for ~25fps
        }
    };


    useEffect(() => {
        if (doAutoScroll) {
            startScrolling();
            return () => clearInterval(intervalRef.current);
        }
        else {
            return
        }
    }, [doAutoScroll, isScrolling]);

    if (singular && laps.length === 0) return null;

    if (type === "sprint") {

        return (
            <div className='scroll-container' style={{ maxHeight: '100%' }} ref={tableRef}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr>
                            <th style={styles.header}>Plaats</th>
                            <th style={styles.header}>Renner</th>
                            <th style={{ ...styles.header, textAlign: 'end' }}>Start {'>'} 200m</th>
                            <th style={{ ...styles.header, textAlign: 'end' }}>200m {'>'} 100m</th>
                            <th style={{ ...styles.header, textAlign: 'end' }}>100m {'>'} Finish</th>
                            <th style={{ ...styles.header, textAlign: 'end' }}>Finish</th>
                        </tr>
                    </thead>
                    <tbody>
                        {laps.sort((a, b) => a[FINISH_TAG] - b[FINISH_TAG]).map((lap, index) => (
                            <tr key={index} style={index === 0 ? styles.stickyRow : {}}>
                                <td>{singular ? singular : index + 1}</td>
                                <td style={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', maxWidth: '650px', width: '650px' }}>
                                    {findTagMapping(lap.tag)}
                                </td>
                                <td style={{ textAlign: 'end' }}>{lap['L03'].toFixed(3)}</td>
                                <td style={{ textAlign: 'end' }}>{(lap['L05'] - lap['L03']).toFixed(3)}</td>
                                <td style={{ textAlign: 'end' }}>{(lap[FINISH_TAG] - lap['L05']).toFixed(3)}</td>
                                <td style={{ textAlign: 'end' }}>{lap[FINISH_TAG].toFixed(3)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    }
    else if (type === "sprint_200") {
        const completedLaps = laps.filter(lap =>
            lap[FINISH_TAG] && lap['L03'] && lap['L05'] && lap[FINISH_TAG] > lap['L05'] && lap['L05'] > lap['L03']
        );


        const fastestLap = completedLaps.reduce((minLap, currentLap) => {
            return currentLap[FINISH_TAG] - currentLap['L03'] < minLap[FINISH_TAG] - minLap['L03'] ? currentLap : minLap;
        }, completedLaps[0]);

        return (
            <div className='scroll-container' style={{ maxHeight: '100%' }} ref={tableRef}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr>
                            <th style={styles.header}>Renner</th>
                            <th style={{ ...styles.header, textAlign: 'end' }}>200m {'>'} 100m</th>
                            <th style={{ ...styles.header, textAlign: 'end' }}>100m {'>'} Finish</th>
                            <th style={{ ...styles.header, textAlign: 'end' }}>Finish</th>
                        </tr>
                    </thead>
                    <tbody>
                        {laps
                            .filter(lap =>
                                (lap['L05'] - lap['L03']) > 0 ||
                                (lap[FINISH_TAG] - lap['L05']) > 0 ||
                                (lap[FINISH_TAG] - lap['L03']) > 0
                            )
                            .sort((a, b) => b["start"] - a["start"])
                            .map((lap, index, completedLaps) => {
                                // make a copy from laps as completed laps and remove all records that are have lap[Finish_TAG] == 0

                                const fastestLap_100m = fastestLap ? fastestLap['L05'] - fastestLap['L03'] : null;
                                const fastestLap_200m = fastestLap ? fastestLap[FINISH_TAG] - fastestLap['L05'] : null;
                                const fastestLap_total = fastestLap ? fastestLap[FINISH_TAG] - fastestLap['L03'] : Infinity;

                                const difference_100m = (lap['L05'] && lap['L03']) ? ((lap['L05'] - lap['L03']) - fastestLap_100m).toFixed(3) : '\u00A0';
                                const difference_200m = (lap[FINISH_TAG] && lap['L05']) ? ((lap[FINISH_TAG] - lap['L05']) - fastestLap_200m).toFixed(3) : '\u00A0';
                                const difference_total = (lap[FINISH_TAG] && lap["L03"]) ? ((lap[FINISH_TAG] - lap["L03"]) - fastestLap_total).toFixed(3) : '\u00A0';

                                const getStyledDifference = (difference) => {
                                    const isPositive = parseFloat(difference) > 0;
                                    return (
                                        <span
                                            style={{
                                                display: 'inline-block',
                                                padding: '2px 6px',
                                                borderRadius: '4px',
                                                backgroundColor: isPositive ? 'red' : 'green',
                                                color: 'white',
                                            }}
                                        >
                                            {isPositive ? "+" : ""}{difference}
                                        </span>
                                    );
                                };

                                // removing laps that have intervals > laptime
                                if (
                                    (lap['L05'] && lap['L03'] && (lap['L05'] - lap['L03']) > MAX_LAPTIME)
                                    || (lap[FINISH_TAG] && lap['L05'] && (lap[FINISH_TAG] - lap['L05']) > MAX_LAPTIME)
                                    || (lap[FINISH_TAG] && lap['L03'] && (lap[FINISH_TAG] - lap['L03']) > MAX_LAPTIME)
                                ) {
                                    return null;
                                }
                                return (
                                    <tr key={index} id={lap.tag + "-" + (new Date(lap.start)).toISOString()} style={index === 0 ? styles.stickyRow : {}}>
                                        <td style={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', maxWidth: '650px', width: '650px' }}>
                                            {findTagMapping(lap.tag)}
                                        </td>
                                        <td style={{ textAlign: 'end' }}>
                                            {lap['L05'] && lap['L03'] ? (lap['L05'] - lap['L03']).toFixed(3) : ''}
                                            <br />
                                            {difference_100m !== "\u00A0" ? getStyledDifference(difference_100m) : difference_100m}
                                        </td>
                                        <td style={{ textAlign: 'end' }}>
                                            {lap[FINISH_TAG] && lap['L05'] ? (lap[FINISH_TAG] - lap['L05']).toFixed(3) : ''}
                                            <br />
                                            {difference_200m !== "\u00A0" ? getStyledDifference(difference_200m) : difference_200m}
                                        </td>
                                        <td style={{ textAlign: 'end' }}>
                                            {lap[FINISH_TAG] && lap['L03'] ? (lap[FINISH_TAG] - lap['L03']).toFixed(3) : ''}
                                            <br />
                                            {difference_total !== "\u00A0" ? getStyledDifference(difference_total) : difference_total}
                                        </td>
                                    </tr>
                                );
                            })}
                    </tbody>
                </table>
                <div style={{ position: "fixed", bottom: '0px', width: "100%", marginTop: '16px' }}>
                    <table style={{ backgroundColor: 'white !important', color: "rgb(0, 111, 183)", fontSize: "4em" }}>
                        <tbody>
                            <tr><td style={{ backgroundColor: 'white', color: "rgb(0, 111, 183)", padding: "0px 8px 0px 8px" }}><span style={{ "fontSize": "0.5em", "lineHeight": "0.5em" }}>Session:</span>
                                {(() => {
                                    const completedLaps = laps.filter(lap => lap[FINISH_TAG] && lap['L03'] && lap['L05']);
                                    if (completedLaps.length === 0) return ' N/A';
                                    const filteredcompletedLaps = completedLaps.filter(lap => lap.L01 > lap.L03);

                                    const fastestLap = filteredcompletedLaps.reduce((minLap, currentLap) => {
                                        return currentLap[FINISH_TAG] - currentLap['L03'] < minLap[FINISH_TAG] - minLap['L03'] ? currentLap : minLap;
                                    }, completedLaps[0]);
                                    return ` ${findTagMapping(fastestLap.tag)}: ${(fastestLap[FINISH_TAG] - fastestLap['L03']).toFixed(3)}`;
                                })()}
                            </td>
                                <td style={{ backgroundColor: 'white', color: "rgb(0, 111, 183)", padding: "0px 8px 0px 8px" }}></td>
                            </tr>
                            {fastest200mAllTime &&
                                <tr>
                                    <td style={{ backgroundColor: 'white', color: "rgb(0, 111, 183)", padding: "0px 8px 0px 8px" }}><span style={{ "fontSize": "0.5em", "lineHeight": "0.5em" }}>All time:</span>
                                        {(() => {
                                            if ("men" in fastest200mAllTime && "tag" in fastest200mAllTime["men"] && "time" in fastest200mAllTime["men"]) {
                                                return ` ${findTagMapping(fastest200mAllTime["men"]["tag"])}: ${fastest200mAllTime["men"]["time"]}`;
                                            }
                                        })()}
                                    </td>
                                    <td style={{ backgroundColor: 'white', color: "rgb(0, 111, 183)", padding: "0px 8px 0px 8px" }}>
                                        {(() => {
                                            if ("women" in fastest200mAllTime && "tag" in fastest200mAllTime["women"] && "time" in fastest200mAllTime["women"]) {
                                                return ` ${findTagMapping(fastest200mAllTime["women"]["tag"])}: ${fastest200mAllTime["women"]["time"]}`;
                                            }
                                        })()}
                                    </td>
                                </tr>}
                        </tbody></table>
                </div>
            </div>
        );
    }
    else if (type === "team_pursuit") {
        // Render a table for team pursuit, it should report the time for L07->L04 and L04->L07 each lap.
        // However, since a lap starts from L01 in table, we need to adjust the logic accordingly.
        // Also, I want to group the laps by team, so we need to find the teams first and then render the laps for each team. A team consists of 4 riders. However, in training there is sometimes a 5th rider, so we need to handle that as well.

        // start from a certain starttime (this will need to be set later)
        // set starttime to the lowest start time of the laps
        if (laps.length === 0) return null; // no laps to display
        var starttime = Math.floor(laps.reduce((min, lap) => Math.min(min, lap.start + lap.L07), Infinity));
        //console.log("starttime",starttime)

        const TIME_BETWEEN_RIDERS = 3000
        const MIN_HALF_LAP_TIME = 15000


        // group half laps by individual rider since the specified starttime
        // every crossing with L04 and L07 is a half lap, separate these immediately
        const groupedLaps = {};
        var dist = {}
        // order laps by start time
        laps.sort((a, b) => a.start - b.start);
        //console.log("laps", laps);
        laps.forEach(lap => {
            if (!lap.tag) return; // skip laps without a tag
            //if (lap.start < starttime) return; // skip laps before the starttime
            if (!groupedLaps[lap.tag]) {
                groupedLaps[lap.tag] = [];
            }


            if (!(lap.tag in dist)) {
                dist[lap.tag] = 0;
            }
            let halfLaptime = 0;

            // push the lap to the groupedLaps
            if (lap['L04']) {
                // check if previous lap exists and if the time since the previous lap is smaller than 10 seconds
                if (groupedLaps[lap.tag].length > 0) {
                    const previousLap = groupedLaps[lap.tag][groupedLaps[lap.tag].length - 1];
                    if ((lap.start + lap.L04 * 1000) - previousLap.utc > MIN_HALF_LAP_TIME) {
                        dist[lap.tag] = 0 // reset distance for this rider
                        starttime = Math.floor(lap.start + lap.L04 * 1000) // reset starttime for this rider
                        halfLaptime = 0
                    }
                    else {
                        halfLaptime = Math.floor(lap.start + lap.L04 * 1000) - previousLap.utc
                    }
                }

                groupedLaps[lap.tag].push({
                    utc: Math.floor(lap.start + lap.L04 * 1000),
                    distance: dist[lap.tag],
                    tag: lap.tag,
                    loop: "L04",
                    halfLaptime: halfLaptime,
                    start: starttime
                });
                dist[lap.tag] += 125
            }
            if (lap['L07']) {
                if (groupedLaps[lap.tag].length > 0) {
                    const previousLap = groupedLaps[lap.tag][groupedLaps[lap.tag].length - 1];
                    if ((lap.start + lap.L07 * 1000) - previousLap.utc > MIN_HALF_LAP_TIME) {
                        dist[lap.tag] = 0
                        starttime = Math.floor(lap.start + lap.L07 * 1000)
                        halfLaptime = 0
                    }
                    else {
                        halfLaptime = Math.floor(lap.start + lap.L07 * 1000) - previousLap.utc
                    }
                }

                groupedLaps[lap.tag].push({
                    utc: Math.floor(lap.start + lap.L07 * 1000),
                    distance: dist[lap.tag],
                    tag: lap.tag,
                    loop: "L07",
                    start: starttime,
                    halfLaptime: halfLaptime
                });
                dist[lap.tag] += 125
            }
        });

        //console.log("groupedLaps", groupedLaps);

        // now, we want to sort to bring all the laps of all riders together in a single array
        // sort this array by utc
        const allLaps = Object.values(groupedLaps)
            .flat()
            .sort((a, b) => a.utc - b.utc);

        // Now, we need to group the laps by UTC time, if difference in UTC time is < 10000 ms, we consider them to be the same lap.
        const groupedByUTC = [];
        allLaps.forEach(lap => {
            const lastGroup = groupedByUTC[groupedByUTC.length - 1];
            if (lastGroup && lap.utc - lastGroup[0].utc < TIME_BETWEEN_RIDERS && lastGroup[0].loop === lap.loop && lastGroup[0].tag !== lap.tag) {
                lastGroup.push(lap);
            } else {
                groupedByUTC.push([lap]);
            }
        });

        console.log("groupedByUTC", groupedByUTC);

        // reverse sort groupedByUTC
        groupedByUTC.sort((a, b) => b[0].utc - a[0].utc);


        // Now we can render the table
        return (
            <div className='scroll-container' style={{ maxHeight: '100%' }} ref={tableRef}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr>
                            <th style={styles.header}>Afstand</th>
                            <th style={styles.header}>Atleten</th>
                            <th style={styles.headerEnd}>Halverondetijd</th>
                            <th style={styles.headerEnd}>Totale tijd</th>
                        </tr>
                    </thead>
                    <tbody>
                        {(groupedByUTC.map((group, index) => (
                            (group.length === 0) ? null :
                                <tr key={index} id={group[0].loop + " " + group[0].utc} style={index === 0 ? styles.stickyRow : {}}>
                                    <td>{Math.max(...group.map(lap => lap.distance))}m</td>

                                    <td style={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', maxWidth: '650px', width: '650px' }}>
                                        {group.map((lap, posIndex) => (
                                            <span key={posIndex}>
                                                {posIndex + 1}: {findTagMapping(lap.tag)}<br />
                                            </span>
                                        ))}
                                    </td>
                                    <td style={{ textAlign: 'end' }}>
                                        {(group[0].halfLaptime / 1000).toFixed(3)}
                                        <br />
                                        {group.slice(1).map((lap, posIndex) => (
                                            <span key={posIndex}>
                                                {group[posIndex + 1].halfLaptime !== 0 ? ((group[posIndex + 1].halfLaptime - group[0].halfLaptime > 0) ? "+" : "") + ((group[posIndex + 1].halfLaptime - group[0].halfLaptime) / 1000).toFixed(3) : ""}
                                                <br />
                                            </span>
                                        ))}
                                    </td>
                                    <td style={{ textAlign: 'end' }}>
                                        {group[0] && "utc" in group[0] && group[0].utc && "start" in group[0] && group[0].start ? (new Date(group[0].utc - group[0].start)).toISOString().substring(14, 23) : ''}
                                        <br />
                                        {group.slice(1).map((lap, posIndex) => (
                                            <span key={posIndex}>
                                                +{((lap.utc - group[posIndex].utc) / 1000).toFixed(3)}<br />
                                            </span>
                                        ))}
                                    </td>
                                </tr>
                        ))
                        )}
                    </tbody>
                </table>
            </div>
        );

    }
});

export default GenericTable;